import { useMemo, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import AccountMenu from "./AccountMenu.jsx";
import ActivityStrip from "./ActivityStrip.jsx";
import MemoryPanel from "./MemoryPanel.jsx";
import SearchPalette from "./SearchPalette.jsx";
import { RECALL } from "../lib/lenses.js";

// When the daily run last found nothing newer in the feed and kept the day (a weekend, a holiday,
// a skipped announcement, a late feed), if that came after the day itself.
const keptAt = day => (day?.kept_at && day.kept_at.slice(0, 10) > day.period ? new Date(day.kept_at) : null);
const utc = (d, opts) => d.toLocaleString("en-GB", { ...opts, timeZone: "UTC" });
const ON = { weekday: "short", day: "numeric", month: "short" };
const CHECKED = { ...ON, hour: "2-digit", minute: "2-digit" };

// The bar every page shares: the tenant's own brand (DEC-023), the project and its enabled
// lenses, and who is signed in. Once a project's day has loaded, it also carries the activity
// strip, search and working memory, all on the project view's shared shell.
export default function Shell({ tenant, project, lenses = [], day, shell, lens }) {
  const brand = tenant.brand?.name || tenant.name;
  const payload = day && day.payload;
  const kept = keptAt(day);
  // On Recall, the bar's panels open things in the day's first lens.
  const dayLens = lenses.some(l => l.id === lens) ? lens : lenses[0]?.id;
  // One bar panel at a time: opening one closes the others; closing one leaves the rest alone.
  const [overlay, setOverlay] = useState(null);
  const [setMemory, setSearch, setAccount] = useMemo(() => {
    const opener = name => open => setOverlay(o => (open ? name : o === name ? null : o));
    return [opener("memory"), opener("search"), opener("account")];
  }, []);

  return (
    <header className={`topbar${project ? " has-project" : ""}`}>
      <Link className="app-mark" to="/" aria-label={`${brand}: all projects`}>
        <svg viewBox="0 0 20 20" aria-hidden="true">
          <path d="M3.5 14.5 L9 5.5 L16 11.5" />
          <circle cx="3.5" cy="14.5" r="2" />
          <circle cx="9" cy="5.5" r="2" />
          <circle className="lit" cx="16" cy="11.5" r="2.6" />
        </svg>
        <span>{brand}</span>
      </Link>

      {project && <span className="app-crumb">{project.name}</span>}

      {project && (
        <nav className="app-lenses" aria-label="Lenses">
          {lenses.map(l => (
            <NavLink key={l.id} to={`/p/${project.slug}/${l.id}`} className="app-lens-tab">
              {l.label}
              <small>{l.phase}</small>
            </NavLink>
          ))}
          <NavLink to={`/p/${project.slug}/${RECALL.id}`} className="app-lens-tab app-recall-tab">
            {RECALL.label}
            <small>{RECALL.phase}</small>
          </NavLink>
        </nav>
      )}

      {shell && <ActivityStrip shell={shell} lens={dayLens} lenses={lenses} />}
      {shell && <SearchPalette shell={shell} lens={dayLens} lenses={lenses} open={overlay === "search"} setOpen={setSearch} />}

      {payload && (
        <span className="app-meta">
          <span>
            {payload.dateLabel} · {payload.totalIncoming} new
          </span>
          {kept ? (
            <b title={`Last checked ${utc(kept, CHECKED)} UTC: the feed had nothing newer, so this is still the newest day.`}>
              Live · nothing new as of {utc(kept, ON)}
            </b>
          ) : (
            <b>{payload.sample ? "Sample data" : "Live"}</b>
          )}
        </span>
      )}

      {shell && <MemoryPanel shell={shell} lens={dayLens} lenses={lenses} open={overlay === "memory"} setOpen={setMemory} />}
      {tenant.user && <AccountMenu user={tenant.user} open={overlay === "account"} setOpen={setAccount} />}
    </header>
  );
}
